/**
 * Sapp's Coaching Contact / CTA Section
 * Design: Navy background with real photo, white form card
 * Conversion: Primary lead capture — name, email, phone, goal, and separate voluntary SMS consent
 */
import { useEffect, useState } from "react";
import { Phone, Mail, CheckCircle2 } from "lucide-react";
import { nativeGhlForm } from "@/lib/ghlForm";

// Background: group training / community energy photo
const BG_IMAGE = "/manus-storage/121641839_3320633357973615_5875540176160323559_n_96920660.jpg";
const MOBILE_FORM_QUERY = "(max-width: 639px)";

function getNativeFormHeight() {
  if (typeof window === "undefined") return nativeGhlForm.desktopFrameHeight;
  return window.matchMedia(MOBILE_FORM_QUERY).matches
    ? nativeGhlForm.mobileFrameHeight
    : nativeGhlForm.desktopFrameHeight;
}

/** Native GoHighLevel form supplied by Sapp's Fitness. */
function NativeGhlForm() {
  const [frameHeight, setFrameHeight] = useState(getNativeFormHeight);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_FORM_QUERY);
    const updateFormHeight = () => setFrameHeight(getNativeFormHeight());

    updateFormHeight();
    mediaQuery.addEventListener("change", updateFormHeight);
    return () => mediaQuery.removeEventListener("change", updateFormHeight);
  }, []);

  return (
    <div
      className="ghl-form-frame overflow-hidden rounded-lg"
      style={{ height: frameHeight }}
    >
      <iframe
        key={`${nativeGhlForm.formId}-${frameHeight}`}
        src={nativeGhlForm.iframeUrl}
        loading="lazy"
        className="w-full !h-full"
        style={{ border: "none", borderRadius: "8px" }}
        id={nativeGhlForm.iframeId}
        data-layout='{"id":"INLINE"}'
        data-trigger-type="alwaysShow"
        data-trigger-value=""
        data-activation-type="alwaysActivated"
        data-activation-value=""
        data-deactivation-type="neverDeactivate"
        data-deactivation-value=""
        data-form-name={nativeGhlForm.formName}
        data-height={String(nativeGhlForm.embedHeight)}
        data-layout-iframe-id={nativeGhlForm.iframeId}
        data-form-id={nativeGhlForm.formId}
        data-cookie-consent="true"
        data-cookie-consent-provider="auto"
        title="Sapp's Fitness consultation request form"
      />
    </div>
  );
}

export default function ContactSection() {
  return (
    <section
      id="contact-section"
      className="relative py-12 lg:py-20 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img loading="lazy"
          src={BG_IMAGE}
          alt="Sapp's Coaching training"
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background: "oklch(0.10 0.04 260 / 0.93)",
          }}
        />
        {/* Blue glow */}
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at 30% 50%, oklch(0.42 0.20 22 / 0.12) 0%, transparent 60%)",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-16">
          {/* Left: Info */}
          <div className="lg:col-span-2">
            <div className="animate-on-scroll eyebrow mb-3">Get Started Today</div>
            <h2
              className="animate-on-scroll animate-on-scroll-delay-1 section-headline text-white mb-6"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}
            >
              Book Your
              <br />
              <span style={{ color: "oklch(0.80 0.17 80)" }}>Book a Free Call</span>
            </h2>
            <p
              className="animate-on-scroll animate-on-scroll-delay-2 text-white/70 leading-relaxed mb-8"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "1.05rem" }}
            >
              Take the first step toward the transformation you've been
              waiting for. Your free breakthrough consultation is a no-pressure
              conversation with a Sapp's Coaching coach — we'll listen to your goals,
              answer your questions, and show you exactly how we can help.
            </p>

            {/* What to expect */}
            <div className="animate-on-scroll animate-on-scroll-delay-3 space-y-3 mb-10">
              {[
                "A personalized conversation about your goals",
                "Honest assessment of which program fits you best",
                "No pressure, no hard sell — just real coaching",
                "Walk away with a clear action plan",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2
                    size={18}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: "oklch(0.80 0.17 80)" }}
                  />
                  <span
                    className="text-white/75"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>

            {/* Contact Info */}
            <div className="animate-on-scroll animate-on-scroll-delay-4 space-y-4">
              <a
                href="tel:+12055830218"
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "oklch(0.42 0.20 22 / 0.2)" }}
                >
                  <Phone size={16} style={{ color: "oklch(0.80 0.17 80)" }} />
                </div>
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Call Us</div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif" }}>(205) 583-0218</div>
                </div>
              </a>
              <a
                href="mailto:sappsfitnessgarage@gmail.com"
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "oklch(0.42 0.20 22 / 0.2)" }}
                >
                  <Mail size={16} style={{ color: "oklch(0.80 0.17 80)" }} />
                </div>
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Email Us</div>
                  <div className="break-all text-sm sm:text-base" style={{ fontFamily: "'DM Sans', sans-serif" }}>sappsfitnessgarage@gmail.com</div>
                </div>
              </a>

            </div>

            {/* GHL Survey CTA */}
            <div className="animate-on-scroll animate-on-scroll-delay-5 mt-8">
              <a
                href="tel:+12055830218"
                className="btn-outline-white inline-flex items-center gap-2"
              >
                Call (205) 583-0218 <Phone size={16} />
              </a>
            </div>
          </div>

          {/* Right: Form — white card */}
          <div className="animate-on-scroll animate-on-scroll-delay-2 lg:col-span-3">
            <div
              id={nativeGhlForm.formAnchorId}
              className="scroll-mt-32 rounded-2xl p-4 sm:p-8"
              style={{
                background: "white",
                boxShadow: "0 20px 60px oklch(0.10 0.04 260 / 0.4)",
              }}
            >
              <h3
                className="section-headline mb-2"
                style={{ fontSize: "1.8rem", color: "oklch(0.18 0.02 40)" }}
              >
                Request Your Free Call
              </h3>
              <p
                className="mb-6 text-sm"
                style={{ fontFamily: "'DM Sans', sans-serif", color: "oklch(0.55 0.01 40)" }}
              >
                Fill out the form below and we&apos;ll reach out within 24 hours.
              </p>
              <NativeGhlForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
